import { z } from "zod";
import { trimmedString } from "./primitives";

export const integrationProviderSchema = z.enum(["azure_devops", "jira", "shortcut", "monday", "linear", "github", "zendesk", "hubspot", "intercom", "salesforce", "slack"]);

export const connectorCatalogProviderSchema = z.enum([
  "azure_devops", "io_spoke_demo", "jira", "linear", "salesforce",
  "zendesk", "github", "slack", "hubspot", "intercom", "shortcut", "monday",
]);
export const connectorCapabilitySchema = z.enum([
  "work.read", "work.create", "work.update", "work.comment", "signal.read", "event.receive", "conversation.interact",
]);
export const connectorManifestSchema = z.object({
  id: connectorCatalogProviderSchema,
  name: z.string().trim().min(1).max(100),
  description: z.string().trim().min(1).max(300),
  category: z.enum(["engineering", "support", "crm", "communication", "operations", "demo"]),
  availability: z.enum(["available", "demo", "planned"]),
  capabilities: z.array(connectorCapabilitySchema).max(7),
  authType: z.enum(["oauth2", "api_token", "app", "none"]),
  setupLabel: z.string().trim().min(1).max(80),
}).strict();
export const connectorCatalogQuerySchema = z.object({
  version: z.enum(["1", "2"]).default("1"),
});
export const connectorCatalogResponseSchema = z.object({
  connectors: z.array(connectorManifestSchema),
});
export const connectorRequestParamsSchema = z.object({
  providerId: connectorCatalogProviderSchema,
});
export const connectorRequestResponseSchema = z.object({
  accepted: z.literal(true),
  providerId: connectorCatalogProviderSchema,
});

export const externalWorkOwnerSchema = z.object({
  type: z.enum(["team", "role", "agent", "human"]),
  id: z.string().trim().min(1).max(200),
}).strict();

export const externalWorkRecordSchema = z.object({
  id: z.string().trim().min(1).max(300),
  type: z.string().trim().min(1).max(100),
  title: z.string().trim().min(1).max(300),
  description: z.string().trim().min(1).max(10_000),
  priority: z.string().trim().min(1).max(100),
  status: z.string().trim().min(1).max(100),
  systems: z.array(z.string().trim().min(1).max(100)).max(50),
  customerImpact: z.string().trim().min(1).max(2_000).optional(),
  owner: externalWorkOwnerSchema.optional(),
  url: z.string().url().max(2_000).optional(),
  updatedAt: z.string().datetime(),
}).strict();

export const externalWorkRecordBatchSchema = z.array(externalWorkRecordSchema).max(100);

export const integrationSyncSourceSchema = z.enum(["customer_execution_demo"]);
export const integrationSyncRunSchema = z.object({
  id: z.string().uuid(),
  source: integrationSyncSourceSchema,
  status: z.enum(["queued", "processing", "completed", "failed"]),
  recordsRead: z.number().int().nonnegative(),
  recordsUpserted: z.number().int().nonnegative(),
  errorMessage: z.string().nullable(),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export const integrationSyncStatusResponseSchema = z.object({
  source: z.object({
    id: integrationSyncSourceSchema,
    name: trimmedString,
    provider: z.literal("mock"),
    capabilities: z.array(connectorCapabilitySchema),
    freshness: z.enum(["never", "fresh", "stale", "syncing", "failed"]),
    lastSuccessfulSyncAt: z.string().datetime().nullable(),
  }),
  latestRun: integrationSyncRunSchema.nullable(),
  recentRuns: z.array(integrationSyncRunSchema),
});
export const triggerIntegrationSyncResponseSchema = z.object({ run: integrationSyncRunSchema });
export const obligationSyncOutcomesSchema = z.object({
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  unchanged: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  failures: z.number().int().nonnegative(),
});
export const connectionSyncRunSchema = z.object({
  id: z.string().uuid(),
  connectionId: z.string().uuid(),
  status: z.enum(["queued", "processing", "retrying", "completed", "failed", "dead_letter"]),
  trigger: z.enum(["manual", "scheduled", "webhook"]),
  attemptCount: z.number().int().nonnegative(),
  maxAttempts: z.number().int().positive(),
  recordsRead: z.number().int().nonnegative(),
  recordsUpserted: z.number().int().nonnegative(),
  errorMessage: z.string().nullable(),
  failureClass: z.string().nullable(),
  durationMs: z.number().int().nonnegative().nullable(),
  providerThrottled: z.boolean(),
  obligationOutcomes: obligationSyncOutcomesSchema.nullable(),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export const connectionSyncStatusResponseSchema = z.object({
  connectionId: z.string().uuid(),
  provider: integrationProviderSchema,
  freshness: z.enum(["never", "fresh", "stale", "syncing", "failed"]),
  lastSuccessfulSyncAt: z.string().datetime().nullable(),
  schedule: z.object({
    enabled: z.boolean(),
    intervalMinutes: z.number().int().min(15).max(10080),
    nextSyncAt: z.string().datetime().nullable(),
  }),
  health: z.enum(["healthy", "degraded", "intervention_required"]),
  consecutiveFailures: z.number().int().nonnegative(),
  latestRun: connectionSyncRunSchema.nullable(),
  recentRuns: z.array(connectionSyncRunSchema),
});
export const triggerConnectionSyncResponseSchema = z.object({ run: connectionSyncRunSchema });
export const updateConnectionSyncScheduleSchema = z.object({
  enabled: z.boolean(),
  intervalMinutes: z.number().int().min(15).max(10080),
}).strict();
export const updateConnectionSyncScheduleResponseSchema = z.object({
  schedule: connectionSyncStatusResponseSchema.shape.schedule,
});

export const azureDevOpsConnectionConfigSchema = z.object({
  organization: trimmedString,
  project: trimmedString,
  workItemType: trimmedString.default("Issue"),
  tenantId: trimmedString,
  clientId: trimmedString,
  clientSecret: trimmedString,
});

export const jiraConnectionConfigSchema = z.object({
  authType: z.literal("oauth2"),
  cloudId: z.string().trim().min(1).max(200),
  siteUrl: z.string().url().max(300).transform((value) => value.replace(/\/$/, "")).refine((value) => {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname.endsWith(".atlassian.net") && !url.username && !url.password && url.pathname === "/" && !url.search && !url.hash;
  }, "Use the HTTPS root URL for a Jira Cloud site ending in .atlassian.net."),
  accessToken: z.string().trim().min(16).max(4_000),
  refreshToken: z.string().trim().min(16).max(4_000),
  accessTokenExpiresAt: z.string().datetime(),
  projectKey: z.string().trim().regex(/^[A-Z][A-Z0-9_]{1,19}$/, "Use a valid uppercase Jira project key."),
  issueType: z.string().trim().min(1).max(100).default("Task"),
  jql: z.string().trim().min(1).max(500).default("statusCategory != Done ORDER BY updated DESC"),
});

export const jiraOAuthStartSchema = z.object({
  connectionId: z.string().uuid().optional(),
  name: z.string().trim().min(1).max(100).default("Jira"),
  projectKey: z.string().trim().regex(/^[A-Z][A-Z0-9_]{1,19}$/, "Use a valid uppercase Jira project key."),
  issueType: z.string().trim().min(1).max(100).default("Task"),
  jql: z.string().trim().min(1).max(500).default("statusCategory != Done ORDER BY updated DESC"),
}).strict();
export const jiraOAuthStartResponseSchema = z.object({ authorizationUrl: z.string().url() }).strict();

export const shortcutConnectionConfigSchema = z.object({
  apiToken: z.string().trim().min(16).max(500),
  workflowStateId: z.number().int().positive(),
  storyType: z.enum(["feature", "bug", "chore"]).default("feature"),
  searchQuery: z.string().trim().min(1).max(500).default("!is:archived"),
});

export const mondayConnectionConfigSchema = z.object({
  apiToken: z.string().trim().min(16).max(1_000),
  boardId: z.string().trim().regex(/^\d+$/, "Use a numeric monday.com board ID."),
  groupId: z.string().trim().min(1).max(200).optional(),
});

export const linearConnectionConfigSchema = z.object({
  apiKey: z.string().trim().min(16).max(1_000),
  teamId: z.string().uuid("Use a valid Linear team ID."),
  createStateId: z.string().uuid("Use a valid Linear workflow state ID.").optional(),
});
const githubName = z.string().trim().regex(/^[A-Za-z0-9_.-]+$/, "Use a valid GitHub owner or repository name.").max(100);
export const githubConnectionConfigSchema = z.object({
  token: z.string().trim().min(16).max(1_000), owner: githubName, repository: githubName,
  labels: z.array(z.string().trim().min(1).max(50)).max(10).default([]),
});
const zendeskSubdomainSchema = z.string().trim().toLowerCase().regex(/^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/, "Use a valid Zendesk subdomain.");
export const zendeskConnectionConfigSchema = z.object({
  authType: z.literal("oauth2"), subdomain: zendeskSubdomainSchema,
  accessToken: z.string().trim().min(16).max(4_000), refreshToken: z.string().trim().min(16).max(4_000),
  accessTokenExpiresAt: z.string().datetime(), tags: z.array(z.string().trim().regex(/^[a-z0-9_-]+$/i).max(50)).max(10).default(["io_spoke"]),
});
export const zendeskOAuthStartSchema = z.object({
  connectionId: z.string().uuid().optional(), name: z.string().trim().min(1).max(100).default("Zendesk"),
  subdomain: zendeskSubdomainSchema, tags: z.array(z.string().trim().regex(/^[a-z0-9_-]+$/i).max(50)).max(10).default(["io_spoke"]),
}).strict();
export const zendeskOAuthStartResponseSchema = z.object({ authorizationUrl: z.string().url() }).strict();
export const hubspotConnectionConfigSchema = z.object({
  authType: z.literal("oauth2"), accessToken: z.string().trim().min(16).max(4_000),
  refreshToken: z.string().trim().min(16).max(4_000), accessTokenExpiresAt: z.string().datetime(),
  accountId: z.string().trim().regex(/^\d+$/).optional(),
});
export const hubspotOAuthStartSchema = z.object({ connectionId: z.string().uuid().optional(), name: z.string().trim().min(1).max(100).default("HubSpot") }).strict();
export const hubspotOAuthStartResponseSchema = z.object({ authorizationUrl: z.string().url() }).strict();
export const intercomConnectionConfigSchema = z.object({
  authType: z.literal("oauth2"), accessToken: z.string().trim().min(16).max(4_000),
  workspaceId: z.string().trim().min(1).max(200), workspaceName: z.string().trim().min(1).max(200),
  region: z.enum(["US", "EU", "AU"]),
});
export const intercomOAuthStartSchema = z.object({ connectionId: z.string().uuid().optional(), name: z.string().trim().min(1).max(100).default("Intercom"), region: z.enum(["US", "EU", "AU"]).default("US") }).strict();
export const intercomOAuthStartResponseSchema = z.object({ authorizationUrl: z.string().url() }).strict();
const salesforceInstanceUrlSchema = z.string().url().max(500).transform((value) => value.replace(/\/$/, "")).refine((value) => { const url = new URL(value); return url.protocol === "https:" && (url.hostname === "salesforce.com" || url.hostname.endsWith(".salesforce.com")) && !url.username && !url.password && url.pathname === "/" && !url.search && !url.hash; }, "Salesforce returned an invalid instance URL.");
export const salesforceConnectionConfigSchema = z.object({ authType: z.literal("oauth2"), accessToken: z.string().trim().min(16).max(4_000), refreshToken: z.string().trim().min(16).max(4_000), instanceUrl: salesforceInstanceUrlSchema, orgId: z.string().trim().min(1).max(100), environment: z.enum(["production", "sandbox"]) });
export const salesforceOAuthStartSchema = z.object({ connectionId: z.string().uuid().optional(), name: z.string().trim().min(1).max(100).default("Salesforce"), environment: z.enum(["production", "sandbox"]).default("production") }).strict();
export const salesforceOAuthStartResponseSchema = z.object({ authorizationUrl: z.string().url() }).strict();
const slackChannelIdSchema = z.string().trim().regex(/^[CG][A-Z0-9]{8,20}$/, "Use a valid Slack public or private channel ID.");
export const slackIdentityStartSchema = z.object({ connectionId: z.string().uuid(), rescueCaseId: z.string().uuid().optional() }).strict();
export const slackConnectionConfigSchema = z.object({ authType: z.literal("oauth2"), accessToken: z.string().trim().min(16).max(4_000), teamId: z.string().trim().min(1).max(100), teamName: z.string().trim().min(1).max(200), channelId: slackChannelIdSchema, channelName: z.string().trim().min(1).max(200), botScopes: z.array(z.string().trim().min(1).max(100)).max(100).optional() });
export const slackOAuthStartSchema = z.object({ connectionId: z.string().uuid().optional(), name: z.string().trim().min(1).max(100).default("Slack"), channelId: slackChannelIdSchema }).strict();
export const slackOAuthStartResponseSchema = z.object({ authorizationUrl: z.string().url() }).strict();

export const createIntegrationConnectionSchema = z.discriminatedUnion("provider", [
  z.object({ provider: z.literal("azure_devops"), name: trimmedString, config: azureDevOpsConnectionConfigSchema, mappings: z.record(trimmedString, trimmedString).optional().default({}) }),
  z.object({ provider: z.literal("jira"), name: trimmedString, config: jiraConnectionConfigSchema, mappings: z.record(trimmedString, trimmedString).optional().default({}) }),
  z.object({ provider: z.literal("shortcut"), name: trimmedString, config: shortcutConnectionConfigSchema, mappings: z.record(trimmedString, trimmedString).optional().default({}) }),
  z.object({ provider: z.literal("monday"), name: trimmedString, config: mondayConnectionConfigSchema, mappings: z.record(trimmedString, trimmedString).optional().default({}) }),
  z.object({ provider: z.literal("linear"), name: trimmedString, config: linearConnectionConfigSchema, mappings: z.record(trimmedString, trimmedString).optional().default({}) }),
  z.object({ provider: z.literal("github"), name: trimmedString, config: githubConnectionConfigSchema, mappings: z.record(trimmedString, trimmedString).optional().default({}) }),
  z.object({ provider: z.literal("zendesk"), name: trimmedString, config: zendeskConnectionConfigSchema, mappings: z.record(trimmedString, trimmedString).optional().default({}) }),
  z.object({ provider: z.literal("hubspot"), name: trimmedString, config: hubspotConnectionConfigSchema, mappings: z.record(trimmedString, trimmedString).optional().default({}) }),
  z.object({ provider: z.literal("intercom"), name: trimmedString, config: intercomConnectionConfigSchema, mappings: z.record(trimmedString, trimmedString).optional().default({}) }),
  z.object({ provider: z.literal("salesforce"), name: trimmedString, config: salesforceConnectionConfigSchema, mappings: z.record(trimmedString, trimmedString).optional().default({}) }),
  z.object({ provider: z.literal("slack"), name: trimmedString, config: slackConnectionConfigSchema, mappings: z.record(trimmedString, trimmedString).optional().default({}) }),
]);

export const exportEntitySchema = z.object({
  entityId: z.string().uuid("Entity id must be a valid UUID."),
});

export const entityIntegrationLinksParamsSchema = z.object({
  entityId: z.string().uuid("Entity id must be a valid UUID."),
});

export const externalResourceLinkSchema = z.object({
  id: z.string().uuid(),
  connectionId: z.string().uuid(),
  connectionName: trimmedString,
  provider: integrationProviderSchema,
  entityId: z.string().uuid(),
  externalType: trimmedString,
  externalId: trimmedString,
  externalUrl: z.string().url().nullable(),
  externalRevision: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const integrationExportResponseSchema = z.object({
  link: externalResourceLinkSchema,
  deduped: z.boolean(),
});

export const externalResourceLinksResponseSchema = z.object({
  links: z.array(externalResourceLinkSchema),
  deliveries: z.array(z.object({
    id: z.string().uuid(),
    connectionId: z.string().uuid(),
    connectionName: trimmedString,
    status: z.enum(["pending", "processing", "completed", "failed"]),
    attemptCount: z.number().int().nonnegative(),
    maxAttempts: z.number().int().positive(),
    errorMessage: z.string().nullable(),
    nextAttemptAt: z.string().datetime().nullable(),
    routingReason: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })).default([]),
});

export type IntegrationDeliveryStatus = z.infer<typeof externalResourceLinksResponseSchema>["deliveries"][number];

export const updateIntegrationConnectionSchema = z.object({
  name: trimmedString.optional(),
  status: z.enum(["active", "disabled"]).optional(),
  isDefault: z.boolean().optional(),
  config: z.union([
    azureDevOpsConnectionConfigSchema.partial().strict(),
    jiraConnectionConfigSchema.partial().strict(),
    shortcutConnectionConfigSchema.partial().strict(),
    mondayConnectionConfigSchema.partial().strict(),
    linearConnectionConfigSchema.partial().strict(),
    githubConnectionConfigSchema.partial().strict(),
    zendeskConnectionConfigSchema.partial().strict(),
    hubspotConnectionConfigSchema.partial().strict(),
    intercomConnectionConfigSchema.partial().strict(),
    salesforceConnectionConfigSchema.partial().strict(),
    slackConnectionConfigSchema.partial().strict(),
  ]).optional(),
  mappings: z.record(trimmedString, trimmedString).optional(),
}).refine((value) => Object.keys(value).length > 0, "At least one change is required.");

export type IntegrationProvider = z.infer<typeof integrationProviderSchema>;
export type ConnectorManifest = z.infer<typeof connectorManifestSchema>;
export type ValidatedExternalWorkRecord = z.infer<typeof externalWorkRecordSchema>;
export type IntegrationSyncRun = z.infer<typeof integrationSyncRunSchema>;
export type IntegrationSyncStatusResponse = z.infer<typeof integrationSyncStatusResponseSchema>;
export type AzureDevOpsConnectionConfig = z.infer<
  typeof azureDevOpsConnectionConfigSchema
>;
export type JiraConnectionConfig = z.infer<typeof jiraConnectionConfigSchema>;
export type ShortcutConnectionConfig = z.infer<typeof shortcutConnectionConfigSchema>;
export type MondayConnectionConfig = z.infer<typeof mondayConnectionConfigSchema>;
export type LinearConnectionConfig = z.infer<typeof linearConnectionConfigSchema>;
export type GitHubConnectionConfig = z.infer<typeof githubConnectionConfigSchema>;
export type ZendeskConnectionConfig = z.infer<typeof zendeskConnectionConfigSchema>;
export type ZendeskOAuthStartPayload = z.infer<typeof zendeskOAuthStartSchema>;
export type HubSpotConnectionConfig = z.infer<typeof hubspotConnectionConfigSchema>;
export type HubSpotOAuthStartPayload = z.infer<typeof hubspotOAuthStartSchema>;
export type IntercomConnectionConfig = z.infer<typeof intercomConnectionConfigSchema>;
export type IntercomOAuthStartPayload = z.infer<typeof intercomOAuthStartSchema>;
export type SalesforceConnectionConfig = z.infer<typeof salesforceConnectionConfigSchema>;
export type SalesforceOAuthStartPayload = z.infer<typeof salesforceOAuthStartSchema>;
export type SlackConnectionConfig = z.infer<typeof slackConnectionConfigSchema>;
export type SlackOAuthStartPayload = z.infer<typeof slackOAuthStartSchema>;
export type JiraOAuthStartPayload = z.infer<typeof jiraOAuthStartSchema>;
export type IntegrationConnectionConfig = AzureDevOpsConnectionConfig | JiraConnectionConfig | ShortcutConnectionConfig | MondayConnectionConfig | LinearConnectionConfig | GitHubConnectionConfig | ZendeskConnectionConfig | HubSpotConnectionConfig | IntercomConnectionConfig | SalesforceConnectionConfig | SlackConnectionConfig;
export type CreateIntegrationConnectionPayload = z.infer<
  typeof createIntegrationConnectionSchema
>;
export type ExportEntityPayload = z.infer<typeof exportEntitySchema>;
export type ExternalResourceLink = z.infer<typeof externalResourceLinkSchema>;
export type UpdateIntegrationConnectionPayload = z.infer<typeof updateIntegrationConnectionSchema>;

export const publicConnectorCatalog: ConnectorManifest[] = [
  { id: "azure_devops", name: "Azure DevOps", category: "engineering", availability: "available", description: "Send approved rescue work into Azure Boards with customer context attached.", capabilities: ["work.create"], authType: "oauth2", setupLabel: "Configure" },
  { id: "io_spoke_demo", name: "IO Spoke Demo Feed", category: "demo", availability: "demo", description: "Experience normalized read synchronization without external credentials.", capabilities: ["work.read"], authType: "none", setupLabel: "Try demo" },
  { id: "jira", name: "Jira", category: "engineering", availability: "available", description: "Connect a Jira Cloud site to synchronize issues and create approved work through secure OAuth consent.", capabilities: ["work.read", "work.create"], authType: "oauth2", setupLabel: "Connect" },
  { id: "shortcut", name: "Shortcut", category: "engineering", availability: "available", description: "Connect Shortcut stories, workflow state, owners, and approved recovery work.", capabilities: ["work.read", "work.create"], authType: "api_token", setupLabel: "Connect" },
  { id: "monday", name: "monday.com", category: "operations", availability: "available", description: "Map flexible monday.com boards into customer execution evidence and approved recovery work.", capabilities: ["work.read", "work.create"], authType: "api_token", setupLabel: "Connect" },
  { id: "linear", name: "Linear", category: "engineering", availability: "available", description: "Synchronize team issues as execution evidence and create approved recovery work.", capabilities: ["work.read", "work.create"], authType: "api_token", setupLabel: "Connect" },
  { id: "salesforce", name: "Salesforce", category: "crm", availability: "available", description: "Bring account, opportunity, renewal, and ownership context into customer-risk analysis without changing CRM records.", capabilities: ["work.read"], authType: "oauth2", setupLabel: "Connect" },
  { id: "zendesk", name: "Zendesk", category: "support", availability: "available", description: "Turn support tickets into rescue evidence and create approved recovery tickets.", capabilities: ["work.read", "work.create"], authType: "oauth2", setupLabel: "Connect" },
  { id: "github", name: "GitHub", category: "engineering", availability: "available", description: "Synchronize repository issues and create approved recovery work with customer context.", capabilities: ["work.read", "work.create"], authType: "api_token", setupLabel: "Connect" },
  { id: "slack", name: "Slack", category: "communication", availability: "available", description: "Start investigations, answer questions, and receive results in Slack or IO Spoke.", capabilities: ["work.read", "conversation.interact"], authType: "oauth2", setupLabel: "Connect" },
  { id: "hubspot", name: "HubSpot", category: "crm", availability: "available", description: "Bring recent deal, renewal, pipeline, and account-owner context into customer-risk analysis.", capabilities: ["work.read"], authType: "oauth2", setupLabel: "Connect" },
  { id: "intercom", name: "Intercom", category: "support", availability: "available", description: "Bring recent customer conversations and support ownership into rescue analysis without changing the inbox.", capabilities: ["work.read"], authType: "oauth2", setupLabel: "Connect" },
];
